import type { Metadata } from "@/hooks/useTokenMetadata";

/**
 * Renders the metadata.attributes grid, with three states:
 *  - revealed + traits → grid of cards
 *  - loading           → skeleton cards
 *  - revealed + empty  → "No traits defined"
 *  - pre-reveal        → render nothing (the parent shows the banner)
 */
export function TraitsGrid({
  metadata,
  metaLoading,
  revealed,
}: {
  metadata: Metadata | null | undefined;
  metaLoading: boolean;
  revealed: boolean;
}) {
  if (!revealed) return null;

  if (metadata?.attributes && metadata.attributes.length > 0) {
    return (
      <section className="mb-6">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Traits
        </h2>
        <div className="grid grid-cols-2 gap-2">
          {metadata.attributes.map((a, i) => (
            <div
              key={`${a.trait_type ?? i}-${i}`}
              className="rounded-md border border-border bg-muted/30 p-2"
            >
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                {a.trait_type ?? "Trait"}
              </div>
              <div className="mt-0.5 text-xs font-medium">{String(a.value ?? "")}</div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (metaLoading) {
    return (
      <section className="mb-6" aria-busy="true" aria-label="Loading traits">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Traits
        </h2>
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-md border border-border bg-muted/30 p-2">
              <div className="h-2.5 w-12 animate-pulse rounded bg-muted" />
              <div className="mt-1.5 h-3 w-20 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (metadata && (!metadata.attributes || metadata.attributes.length === 0)) {
    return <p className="mb-6 text-xs text-muted-foreground">No traits defined for this token.</p>;
  }

  return null;
}
